import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import api from '../api/client';
import { Store, Branch, BranchProduct, Responsible, Theme } from '../types';
import { useAnalytics } from './AnalyticsContext';

interface ShopContextType {
  stores: Store[];
  selectedStore: Store | null;
  selectedBranch: Branch | null;
  branchProducts: BranchProduct[];
  loading: boolean;
  error: string | null;
  branches: Branch[];
  adminBranches: Branch[];
  managers: Responsible[];
  themes: Theme[];
  setSelectedStore: (store: Store | null) => void;
  setSelectedBranch: (branch: Branch | null) => void;
  fetchStores: () => Promise<void>;
  fetchBranchProducts: (branchId: number) => Promise<void>;
  fetchAdminBranches: () => Promise<void>;
  fetchManagers: () => Promise<void>;
  createBranch: (data: Partial<Branch>) => Promise<Branch | null>;
  updateBranch: (id: number, data: Partial<Branch>) => Promise<Branch | null>;
  deleteBranch: (id: number) => Promise<boolean>;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

export const ShopProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branchProducts, setBranchProducts] = useState<BranchProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [adminBranches, setAdminBranches] = useState<Branch[]>([]);
  const [managers, setManagers] = useState<Responsible[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const { trackEvent } = useAnalytics();

  // Load initial state from cookies
  useEffect(() => {
    const storedStores = Cookies.get('stores');
    const storedSelectedStore = Cookies.get('selectedStore');
    const storedSelectedBranch = Cookies.get('selectedBranch');

    if (storedStores) setStores(JSON.parse(storedStores));
    if (storedSelectedStore) setSelectedStore(JSON.parse(storedSelectedStore));
    if (storedSelectedBranch) setSelectedBranch(JSON.parse(storedSelectedBranch));

    fetchStores();
    fetchAdminBranches();
  }, []);

  // Persist state to cookies
  useEffect(() => {
    if (stores.length > 0) Cookies.set('stores', JSON.stringify(stores), { expires: 7 });
    if (selectedStore) Cookies.set('selectedStore', JSON.stringify(selectedStore), { expires: 7 });
    if (selectedBranch) Cookies.set('selectedBranch', JSON.stringify(selectedBranch), { expires: 7 });
  }, [stores, selectedStore, selectedBranch]);

  const fetchStores = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<{ results: Store[] }>('/stores/');
      const fetchedStores = response.data.results || [];
      setStores(fetchedStores);

      const storeThemes = fetchedStores.filter(s => s.theme).map(s => s.theme as Theme);
      setThemes(storeThemes);

      if (!selectedStore && fetchedStores.length > 0) {
        setSelectedStore(fetchedStores[0]);
        if (fetchedStores[0].branches?.length > 0) {
          setSelectedBranch(fetchedStores[0].branches[0]);
          setBranches(fetchedStores.flatMap(store => store.branches || []));
          await fetchBranchProducts(fetchedStores[0].branches[0].id);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch stores');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranchProducts = async (branchId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<{ results: BranchProduct[] }>('/products/', { params: { branch: branchId } });
      setBranchProducts(response.data.results || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch branch products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminBranches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<{ results: Branch[] }>('/branches/', { withCredentials: true });
      setAdminBranches(response.data.results || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch admin branches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<{ results: Responsible[] }>('/responsibles/', { withCredentials: true });
      setManagers(resp.data.results || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch managers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createBranch = async (data: Partial<Branch>) => {
    setLoading(true);
    try {
      const response = await api.post<Branch>('/branches/', data);
      await fetchAdminBranches();
      return response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to create branch');
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateBranch = async (id: number, data: Partial<Branch>) => {
    setLoading(true);
    try {
      const response = await api.put<Branch>(`/branches/${id}/`, data);
      await fetchAdminBranches();
      return response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to update branch');
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteBranch = async (id: number) => {
    setLoading(true);
    try {
      await api.delete(`/branches/${id}/`);
      await fetchAdminBranches();
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to delete branch');
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };
  // 🔹 Listener مركزي على تغيير الـ Store المختار
  useEffect(() => {
    if (selectedStore) {
      trackEvent('select_store', {
        store_id: selectedStore.id,
        store_name: selectedStore.name,
      });
    }
  }, [selectedStore, ]);

  // 🔹 Listener مركزي على تغيير الـ Branch المختار
  useEffect(() => {
    if (selectedBranch) {
      trackEvent('select_branch', {
        branch_id: selectedBranch.id,
        branch_name: selectedBranch.name,
        store_id: selectedStore?.id,
      });
    }
  }, [selectedBranch, selectedStore, ]);

  // 🔹 Listener مركزي على تحديث المنتجات في الـ Branch
  useEffect(() => {
    if (branchProducts.length > 0) {
      trackEvent('branch_products_loaded', {
        branch_id: selectedBranch?.id,
        products_count: branchProducts.length,
      });
    }
  }, [branchProducts, selectedBranch,]);
  return (
    <ShopContext.Provider
      value={{
        stores,
        branches,
        adminBranches,
        managers,
        selectedStore,
        setSelectedStore,
        selectedBranch,
        setSelectedBranch,
        themes,
        branchProducts,
        loading,
        error,
        fetchStores,
        fetchBranchProducts,
        fetchAdminBranches,
        fetchManagers,
        createBranch,
        updateBranch,
        deleteBranch,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShopContext = () => {
  const context = useContext(ShopContext);
  if (!context) throw new Error('useShopContext must be used within a ShopProvider');
  return context;
};
